<template>
	<div>
		<h5 class="mb-4 font-weight-bold">Export Scheduler</h5>
		<div>
			<div class="mx-n2 mb-3 d-md-flex">
				<div class="w-100 px-2">
					<label class="text-sm" for="start-date">Start Date <span class="text-danger">*</span></label>
					<DatePicker
						v-model="modelValue.start_date"
						class="w-100"
						:disabled-date="disabledStartDates"
						format="MM-DD-YYYY"
						:input-attr="{ id: 'start-date' }"
						input-class="form-control form-control-sm rounded-sm"
						prefix-class="xmx"
					/>
				</div>
				<div class="w-100 px-2">
					<label class="text-sm" for="end-date">End Date <span class="text-muted">(optional)</span></label>

					<DatePicker
						v-model="modelValue.end_date"
						class="w-100"
						:disabled-date="disabledEndDates"
						format="MM-DD-YYYY"
						:input-attr="{ id: 'end-date' }"
						input-class="form-control form-control-sm rounded-sm"
						prefix-class="xmx"
					/>
				</div>
			</div>
		</div>

		<div class="mx-n2 mb-3 d-md-flex">
			<div class="w-100 px-2">
				<base-select
					v-model="modelValue.occurrence"
					input-classes="form-control-sm"
					label="Frequency"
					label-classes="text-sm"
					:options="frequencyOptions"
				></base-select>
			</div>
			<div v-if="['Weekly', 'Monthly'].includes(modelValue.occurrence)" class="w-100 px-2">
				<!-- Weekly has a single pill row and no label above it, so add a
				     spacer to line it up with the Frequency dropdown. Monthly
				     already has the First/Last row filling that space. -->
				<div v-if="'Weekly' == modelValue.occurrence" aria-hidden="true" style="height: 2.1rem"></div>
				<div v-if="'Monthly' == modelValue.occurrence" class="d-flex align-items-center mb-2">
                    <GroupedOptions
                        v-model="modelValue.day_of_month"
                        button-classes="dom-option text-primary bg-transparent py-1 pr-0 pl-0 mr-3 text-left text-capitalize position-relative"
                        class="exports-options w-50 mr-auto"
                        :options="weekFrequencyOptions"
                    />
                </div>
				<GroupedOptions
					v-model="modelValue.day_of_week"
					button-classes="badge badge-pill text-capitalize mr-3 mb-2"
					class="d-flex flex-wrap align-items-center"
                    :native-type="'Weekly' == modelValue.occurrence ? 'checkbox' : 'radio'"
					:options="dayOptions"
				/>
			</div>
		</div>
			<div class="mx-n2 d-md-flex">
				<div class="w-100 px-2">
					<label class="text-sm d-block" for="time">Time of Day</label>
					<GroupedOptions
						v-model="timeOfDaySlot"
						button-classes="badge badge-pill text-capitalize mr-2 mb-2 m-sm-2"
						class="d-sm-flex"
						native-type="radio"
						:options="timeSlotOptions"
					/>
				</div>
				<div class="w-100 px-2">
					<base-select
						v-model="modelValue.timezone"
						input-classes="form-control-sm"
						label="Timezone"
						label-classes="text-sm"
						:options="timezoneOptions"
					></base-select>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import timezones from 'timezones.json';
import DatePicker from 'vue2-datepicker';
import GroupedOptions from '@components/GroupedOptions';

// Morning/Evening are the only time-of-day options users pick; each maps to a
// fixed clock time that gets stored as the schedule's time_of_day.
const TIME_SLOTS = {
	Morning: { hour: 5, minute: 0 },   // 5:00 AM
	Evening: { hour: 22, minute: 0 },  // 10:00 PM
};

export default {
	props: {
		value: {
			type: Object,
			default: () => ({
				start_date: new Date(),
				end_date: null,
				occurrence: 'Daily',
                day_of_month: 'First',
				day_of_week: [],
				time_of_day: null,
				timezone: moment.tz.guess(),
			}),
		},
	},
	components: {
		DatePicker,
		GroupedOptions,
	},
	computed: {
		modelValue: {
			get() {
				return this.value;
			},
			set(value) {
				this.$emit('input', value);
			},
		},
		frequencyOptions() {
			return [
				{ text: 'Daily', value: 'Daily' },
				{ text: 'Weekly', value: 'Weekly' },
				{ text: 'Monthly', value: 'Monthly' },
			];
		},
        weekFrequencyOptions() {
            return [
                { label: 'First', value: 'First' },
                { label: 'Last', value: 'Last' }
            ]
        },
		dayOptions() {
			return [
				{ label: 'Mon', value: 'Monday' },
				{ label: 'Tue', value: 'Tuesday' },
				{ label: 'Wed', value: 'Wednesday' },
				{ label: 'Thurs', value: 'Thursday' },
				{ label: 'Fri', value: 'Friday' },
				{ label: 'Sat', value: 'Saturday' },
				{ label: 'Sun', value: 'Sunday' },
			];
		},
		timeSlotOptions() {
			return [
				{ label: 'Morning', value: 'Morning' },
				{ label: 'Evening', value: 'Evening' },
			];
		},
		// Users pick Morning/Evening only; we store time_of_day as a real time
		// so the schedule builder (celery beat) and the parent's
		// moment(...).format('h:mm A') keep working with no downstream changes.
		timeOfDaySlot: {
			get() {
				const t = this.modelValue.time_of_day;
				if (!t) return null;
				// A restored schedule may bring time_of_day back as the stored
				// "h:mm A" string; a fresh pick is a Date. Handle both.
				const m = typeof t === 'string' ? moment(t, ['h:mm A', moment.ISO_8601]) : moment(t);
				if (!m.isValid()) return null;
				return m.hour() < 12 ? 'Morning' : 'Evening';
			},
			set(slot) {
				const time = TIME_SLOTS[slot];
				this.modelValue.time_of_day = time
					? moment().hour(time.hour).minute(time.minute).second(0).millisecond(0).toDate()
					: null;
			},
		},
		timezoneOptions() {
			return timezones
				.filter((item) => item.utc.length)
				.map((item) => {
					const guessedTz = moment.tz.guess().replaceAll(' ', '_')

					return {
						text: item.text,
						value: item.utc.includes(guessedTz)
							? guessedTz
							: item.utc[0],
					};
				})
				
				// .reduce((prev, cur) => prev.find(item => item.value === cur.value) ? prev : [...prev, cur], [])
		},
	},
	methods: {
		disabledStartDates(date) {
			if (this.modelValue.end_date)
				return (
					moment().subtract(1, 'days') >= date ||
					this.modelValue.end_date <= date
				);
			else return moment().subtract(1, 'days') >= date;
		},
		disabledEndDates(date) {
			if (this.modelValue.start_date) return this.modelValue.start_date >= date;
			else return moment() >= date;
		},
	},
    mounted() {
        // Start Date defaults to today so the picker always shows a selected
        // date (it's required). Never overwrite a date already set (e.g. an
        // amended schedule with its own start date).
        if (!this.modelValue.start_date)
            this.modelValue.start_date = new Date();

        // Default Time of Day to Morning so a slot is highlighted for every
        // frequency (incl. Daily) on load, rather than showing blank. Uses the
        // setter so it's stored in the same shape the picker produces. Never
        // overwrite an existing selection (e.g. an amended schedule set to Evening).
        if (!this.modelValue.time_of_day)
            this.timeOfDaySlot = 'Morning';

        // Seed defaults if the scheduler opens already in Weekly/Monthly (e.g.
        // amending a saved schedule) so the pickers are highlighted on load,
        // not just when the user switches frequency. Never overwrite an
        // existing selection.
        const occ = this.modelValue.occurrence;
        if ('Weekly' == occ) {
            if (!Array.isArray(this.modelValue.day_of_week) || !this.modelValue.day_of_week.length)
                this.modelValue.day_of_week = ['Monday'];
        } else if ('Monthly' == occ) {
            if (!this.modelValue.day_of_month)
                this.modelValue.day_of_month = 'First';
            if (!this.modelValue.day_of_week || Array.isArray(this.modelValue.day_of_week))
                this.modelValue.day_of_week = 'Monday';
        }
    },
    watch: {
        // When the user switches frequency, seed the day pickers with a default
        // so a choice is pre-selected/highlighted rather than blank.
        //   Weekly  → day_of_week defaults to ['Monday'] (checkbox → array)
        //   Monthly → day_of_month defaults to 'First', day_of_week to 'Monday'
        //             (Monthly's day-of-week picker is a radio → string)
        'modelValue.occurrence'(newValue) {
            if ('Weekly' == newValue) {
                if (!Array.isArray(this.modelValue.day_of_week) || !this.modelValue.day_of_week.length)
                    this.modelValue.day_of_week = ['Monday']
            } else if ('Monthly' == newValue) {
                if (!this.modelValue.day_of_month)
                    this.modelValue.day_of_month = 'First'
                if (!this.modelValue.day_of_week || Array.isArray(this.modelValue.day_of_week))
                    this.modelValue.day_of_week = 'Monday'
            }
        }
    }
};
</script>

<style scoped>
/* First/Last stay plain text; the selected one highlights (bold + brand
   colour + underline) instead of a pill background. GroupedOptions puts
   .active on the label of the selected option. */
::v-deep .dom-option {
    opacity: 0.6;
    font-weight: 500;
}
::v-deep .dom-option.active {
    opacity: 1;
    font-weight: 700;
    text-decoration: underline;
}
</style>
